import router from '@/router'
import axios from '@/lib/API/client'
import Memory from '@/lib/memory'
import Time from '@/lib/helpers/time'
import { i18n, loadTranslations } from '@/i18n'
import * as Sentry from '@sentry/vue'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor'
import { isBiometricAvailable, verifyBiometric } from '@/lib/biometric'
import { buildLoginRedirectLocation, getPostAuthRedirectTarget } from '@/lib/helpers/deeplink'
import { hasSubscriptionAccess as userHasSubscriptionAccess } from '@/lib/premium-access'
import { identifyRevenueCatUser, revenueCatAppUserId } from '@/lib/revenuecat-identity'
import { keepBadgeCount } from '@/lib/invitations'

// Two loads can overlap when the identified account changes. Only the latest
// one may write to the store, otherwise the slower request commits the
// offerings of an account that is no longer signed in.
let purchasesRequestId = 0
let identifiedAppUserId = null
// A counter cancels a stale write, but it cannot stop native calls that are
// already in flight: two overlapping logIn() can still leave the SDK bound to
// the wrong account, and a purchase would be billed to that profile. Chaining
// the loads guarantees one at a time.
let purchasesChain = Promise.resolve()

const isDifferentAccount = (current, next) => Boolean(current?.id && next?.id && String(current.id) !== String(next.id))

const clearAuthStorage = () =>
    Promise.all([
        Memory.remove('auth_token'),
        Memory.remove('auth_expire_at'),
        Memory.remove('auth_refresh_token'),
        Memory.removeSecure('auth_refresh_token'),
    ])

export default {
    namespaced: true,
    state: () => {
        return {
            /**
             * Stores the current logged in user.
             */
            user: undefined,

            /**
             * Stores the authentication token of
             * the application.
             */
            token: undefined,

            /**
             * Stores the refresh token of the
             * application.
             */
            refresh_token: undefined,

            /**
             * Stores the expiration time of the token.
             *
             * time: Date instance with the time it expires.
             * handler: Handler that will update the token before
             *  expiration happens
             */
            expires: { time: undefined, handler: undefined },

            /**
             * Determines if the initial check has been done.
             */
            initialCheckDone: false,

            /**
             * Prevents concurrent biometric login attempts.
             */
            biometricInProgress: false,

            offerings: [],
        }
    },

    getters: {
        /**
         * Determines if the user is logged in or not.
         */
        loggedIn(state) {
            return (
                typeof state.user !== 'undefined' &&
                typeof state.token !== 'undefined' &&
                typeof state.refresh_token !== 'undefined' &&
                typeof state.expires.time !== 'undefined' &&
                typeof state.expires.handler !== 'undefined'
            )
        },

        hasSubscriptionAccess(state) {
            return userHasSubscriptionAccess(state.user)
        },
    },

    mutations: {
        /**
         * Change the state to determine if the
         * authentication has performed the initial check.
         */
        setInitialCheck(state, value) {
            state.initialCheckDone = value
        },

        /**
         * Sets the biometric in progress flag.
         */
        setBiometricInProgress(state, value) {
            state.biometricInProgress = value
        },

        /**
         * Sets the user to the state.
         */
        setUser(state, { user, token, refresh_token }) {
            // Only mutate the user if the request
            // contains the user. This must be checked
            // because this method is also called when
            // the token refreshes and the token refresh
            // endpoint does not return the user information.
            if (user) state.user = keepBadgeCount(state.user, user)
            // Stores the authentication tokens
            // We also need to conditionally check this
            // since this method is also called when the user
            // edits his account info and not the tokens.
            if (token) state.token = token
            if (refresh_token) state.refresh_token = refresh_token
        },

        /**
         * Sets the available offering
         */
        setOfferings(state, { offerings }) {
            state.offerings = offerings
        },

        /**
         * Sets the expiration information.
         */
        setExpires(state, { time, handler }) {
            if (state.expires.handler) {
                clearTimeout(state.expires.handler)
            }
            state.expires.time = time
            state.expires.handler = handler
        },

        /**
         * Clears the user authentication information.
         */
        clear(state) {
            if (state.expires.handler) {
                clearTimeout(state.expires.handler)
            }
            state.user = undefined
            state.token = undefined
            state.refresh_token = undefined
            state.expires.time = undefined
            state.expires.handler = undefined
            state.offerings = []
        },
    },

    actions: {
        /**
         * Updates the user information on the server
         * and updates the local copy.
         */
        async edit({ commit, dispatch }, info) {
            // Perform a server update
            const response = await axios.put('/user', info)
            // Update the local copy of the user.
            commit('setUser', { user: response.data })
            return response
        },
        /**
         * Update the local copy of the user
         */
        setUser({ commit }, user) {
            commit('setUser', { user })
        },
        /**
         * Performs the initial check of the user
         * authenticaton. This must be called upon
         * the given layout to initiate the authentication.
         */
        async initialCheck({ state, commit, getters, dispatch }) {
            // Check if there's a token inside the phone's memory.
            const token = await Memory.get('auth_token')
            const expiresIn = await Memory.get('auth_expire_at')
            let refresh_token = await Memory.getSecure('auth_refresh_token')
            if (!refresh_token) {
                const legacyRefreshToken = await Memory.get('auth_refresh_token')
                if (legacyRefreshToken) {
                    refresh_token = legacyRefreshToken
                    const storedSecure = await Memory.setSecure('auth_refresh_token', legacyRefreshToken)
                    if (storedSecure) {
                        await Memory.remove('auth_refresh_token')
                    }
                }
            }
            const guest = router?.currentRoute?.value?.meta?.guest
            let user = undefined
            let refreshed = false
            if (token && expiresIn && refresh_token && !guest) {
                const biometricEnabled = await Memory.get('auth_biometric_enabled', { otherwise: false })
                if (biometricEnabled) {
                    const availability = await isBiometricAvailable()
                    if (availability?.available) {
                        await loadTranslations()
                        await router.push(buildLoginRedirectLocation(router.currentRoute.value))
                        return commit('setInitialCheck', true)
                    }
                }
                // There's a token and an expiration at. We need to check
                // if it has expired and if the token is valid.
                const expire_at_date = new Time(expiresIn)
                if (expire_at_date.isPast()) {
                    refreshed = await dispatch('refresh', { refresh_token })
                    if (!refreshed) {
                        await clearAuthStorage()
                        await loadTranslations()
                        await router.push(buildLoginRedirectLocation(router.currentRoute.value))
                        return commit('setInitialCheck', true)
                    }
                    user = state.user
                }
                if (!user && !refreshed) {
                    // The expiration time is valid, but we need to check
                    // if the token is valid and grab the user info from the server.
                    user = await dispatch('user', { token })
                }

                if (user !== undefined && !refreshed) {
                    // Looks like the token is valid and we now have the
                    // user information. We can already set the data to the auth.
                    await dispatch('saveUser', { user, token, refresh_token, forceExpiresIn: expiresIn })
                }
            }
            if (state.initialCheckDone) {
                return
            }

            // Check if there's the need for perform redirects
            if (getters.loggedIn && guest) {
                await router.push(getPostAuthRedirectTarget(router.currentRoute.value))
            } else if (!getters.loggedIn && !guest) {
                await router.push(buildLoginRedirectLocation(router.currentRoute.value))
            }
            await loadTranslations(user?.locale)

            commit('setInitialCheck', true)
        },

        /**
         * Returns a promise that resolves to the
         * offering available
         */
        loadPurchases(_context, { user }) {
            const requestId = ++purchasesRequestId

            // A rejected predecessor must not break the chain for the next load.
            const swallow = () => undefined

            purchasesChain = purchasesChain
                .catch(swallow)
                .then(() => this.dispatch('auth/runPurchasesLoad', { user, requestId }))

            return purchasesChain
        },

        async runPurchasesLoad({ commit }, { user, requestId }) {
            console.log('#PURCHASES loadPurchases', user)

            const appUserId = revenueCatAppUserId(user)
            const isCurrentRequest = () => requestId === purchasesRequestId

            // Only drop the offerings when the account actually changes: doing it
            // on every refresh of the same user emptied the paywall for the time
            // the reload took.
            if (appUserId !== identifiedAppUserId) {
                commit('setOfferings', { offerings: [] })
                identifiedAppUserId = appUserId
            }

            if (Capacitor.getPlatform() !== 'ios') {
                return
            }

            try {
                // await Purchases.setMockWebResults({ shouldMockWebResults: true })
                await Purchases.setLogLevel({ level: LOG_LEVEL.VERBOSE })
                const identified = await identifyRevenueCatUser({
                    purchases: Purchases,
                    user,
                    apiKey: import.meta.env?.VITE_APP_APPLE_REVENUECAT_API_KEY,
                    shouldContinue: isCurrentRequest,
                })
                if (!identified) {
                    console.warn('#PURCHASES missing API key or user identity')
                    return
                }

                const customerInfo = await Purchases.getCustomerInfo()
                console.log('#PURCHASES getCustomerInfo', customerInfo)
                console.log('#PURCHASES getCustomerInfo.activeSubscriptions', customerInfo?.activeSubscriptions)

                const offerings = await Purchases.getOfferings()
                console.log('#PURCHASES offerings', offerings)
                if (!isCurrentRequest()) {
                    console.warn('#PURCHASES stale load ignored')
                    return
                }

                if (offerings?.current?.availablePackages?.length > 0) {
                    // offerings.current.availablePackages
                    commit('setOfferings', { offerings: offerings.current.availablePackages })
                } else {
                    console.log('Aucune offre disponible') //this is normal from a web browser
                    commit('setOfferings', { offerings: [] })
                }
            } catch (error) {
                console.warn('Purchases', error)
            }
        },

        /**
         * Returns a promise that resolves to the
         * authenticated user or undefined. Additionally
         * it accepts a token.
         */
        user({ state }, { token }) {
            return axios
                .get('/user', { headers: { Authorization: `Bearer ${token ?? state.token}` } })
                .then((response) => response.data)
                .catch(() => undefined)
        },

        async refresh({ dispatch }, { refresh_token }) {
            try {
                const response = await axios.post('/refresh-token', { refresh_token })
                const payload = response?.data ?? {}
                const token = payload?.token
                const nextRefreshToken = payload?.refresh_token ?? refresh_token
                let user = payload?.user

                if (!token) return false
                if (!user) {
                    user = await dispatch('user', { token })
                    if (!user) return false
                }

                await dispatch('saveUser', { ...payload, token, refresh_token: nextRefreshToken, user })
                return { ...payload, token, refresh_token: nextRefreshToken, user }
            } catch (error) {
                return false
            }
        },

        /**
         * Saves the user information and the expiration
         * time for the next token-refresh. This function
         * is abstracted correctly to perform additional
         * recursive calls by using the setTimeout function.
         * This means that this function is called on both,
         * the login and the token-refresh actions.
         */
        async saveUser(
            { state, commit, dispatch },
            { user, token, refresh_token, expiresIn, forceExpiresIn = undefined, impersonate = false }
        ) {
            const nextRefreshToken = refresh_token ?? state.refresh_token
            let notificationPurge
            if (isDifferentAccount(state.user, user)) {
                const [{ forgetNotifications }, { purgeFeature }] = await Promise.all([
                    import('@/lib/hooks/use-notifications'),
                    import('@/lib/sqlite'),
                ])
                // An impersonation skips logout: invalidate old requests and
                // copies before switching the token, without purging recordings.
                forgetNotifications()
                notificationPurge = purgeFeature('notifications')
            }
            commit('setUser', { user, token, refresh_token: nextRefreshToken })
            await notificationPurge

            // Only GET /user carries the invitations badge (e.g. not POST /login):
            // fetch it once, without holding the sign-in back.
            if (user && state.user?.pending_invitations_count === undefined) {
                dispatch('user', { token }).then((fresh) => {
                    if (fresh?.pending_invitations_count === undefined) return
                    if (String(fresh.id) !== String(state.user?.id)) return
                    commit('setUser', {
                        user: { ...state.user, pending_invitations_count: fresh.pending_invitations_count },
                    })
                })
            }

            // Convert from seconds to milliseconds
            // the expiration time of the token.
            const expiresInMilliseconds = expiresIn * 1000
            const refreshToken = () =>
                axios
                    .post('/refresh-token', { refresh_token: nextRefreshToken })
                    .then((response) => dispatch('saveUser', response.data))
                    .catch(function (error) {
                        console.log('refresh token error', error)
                        router.push(buildLoginRedirectLocation(router.currentRoute.value))
                    })

            const refreshSkewInMilliseconds = 1000 * 60 * 5 // 5 minutes
            const fExpireIn = new Time(forceExpiresIn)
            const fExpireInDate = forceExpiresIn ? new Date(fExpireIn.time.toISOString()) : undefined
            const expiresAt = fExpireInDate ?? new Date(Date.now() + expiresInMilliseconds)
            const refreshAt = new Date(expiresAt.getTime() - refreshSkewInMilliseconds)
            let delay = refreshAt - Date.now()
            // https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout#maximum_delay_value
            const maximumDelay = 1000 * 60 * 60 * 24 // 24 hours
            if (delay > maximumDelay) {
                delay = maximumDelay
            }
            if (delay < 0) {
                delay = 0
            }
            commit('setExpires', {
                time: expiresAt,
                handler: setTimeout(refreshToken, delay),
            })
            // To identify the user in Sentry:
            if (user) {
                Sentry.setUser({ id: user?.id, email: user?.email, username: user?.first_name + ' ' + user?.last_name })

                if (impersonate) {
                    // An admin preview must never create or switch a RevenueCat customer.
                    // Invalidate any load still running first, otherwise it puts
                    // the previous account's offerings back after this clear.
                    purchasesRequestId++
                    identifiedAppUserId = null
                    commit('setOfferings', { offerings: [] })
                } else {
                    await dispatch('loadPurchases', { user })

                    // date.toISOString().slice(0, 19).replace('T', ' ')
                    Device.getInfo().then((info) => {
                        let userInfo = {
                            last_seen: new Date().toISOString().slice(0, 19).replace('T', ' '),
                            last_platform: info['operatingSystem'],
                        }
                        if (Capacitor.getPlatform() === 'web') {
                            userInfo['webapp_version'] = APP_VERSION
                        } else {
                            userInfo['mobile_version'] = APP_VERSION
                        }

                        //here we don't care to wait for the response
                        axios.put('/user', { ...userInfo }).catch(() => undefined)
                    })
                }
            }

            // Save the user to the phone's memory
            const persistPromises = [Memory.set('auth_token', token), Memory.set('auth_expire_at', expiresAt)]
            if (nextRefreshToken) {
                const storedSecure = await Memory.setSecure('auth_refresh_token', nextRefreshToken)
                if (storedSecure) {
                    persistPromises.push(Memory.remove('auth_refresh_token'))
                }
            }
            return Promise.all(persistPromises)
        },

        /**
         * Perform a forgot action against the server API.
         */
        async forgot({ dispatch }, { credentials }) {
            const response = await axios.post('/password/email', { email: credentials.email })
            return response.data
        },

        /**
         * Perform a exist action against the server API.
         */
        async exist({ dispatch }, { credentials }) {
            const response = await axios.post('/login/exist', credentials, { withCredentials: true })
            return response.data
        },

        /**
         * Attempts to authenticate using stored tokens and biometric verification.
         * Returns { ok: boolean, reason: string }.
         */
        async biometricLogin({ state, commit, dispatch }) {
            // Prevent concurrent biometric login attempts (race condition on fast Face ID)
            if (state.biometricInProgress) {
                return { ok: false, reason: 'in-progress' }
            }
            commit('setBiometricInProgress', true)

            try {
                const token = await Memory.get('auth_token')
                const expiresIn = await Memory.get('auth_expire_at')
                let refresh_token = await Memory.getSecure('auth_refresh_token')
                if (!refresh_token) {
                    const legacyRefreshToken = await Memory.get('auth_refresh_token')
                    if (legacyRefreshToken) {
                        refresh_token = legacyRefreshToken
                        const storedSecure = await Memory.setSecure('auth_refresh_token', legacyRefreshToken)
                        if (storedSecure) {
                            await Memory.remove('auth_refresh_token')
                        }
                    }
                }

                if (!token || !expiresIn || !refresh_token) {
                    return { ok: false, reason: 'missing' }
                }

                const biometricEnabled = await Memory.get('auth_biometric_enabled', { otherwise: false })
                if (!biometricEnabled) {
                    return { ok: false, reason: 'disabled' }
                }

                const availability = await isBiometricAvailable()
                if (!availability?.available) {
                    return { ok: false, reason: 'unavailable' }
                }

                const reason = i18n?.global?.t?.('security.biometric-required') ?? 'Authenticate to continue'
                const verification = await verifyBiometric({ reason })
                if (!verification?.verified) {
                    return { ok: false, reason: verification?.canceled ? 'canceled' : 'failed' }
                }

                const expire_at_date = new Time(expiresIn)
                if (expire_at_date.isPast()) {
                    const refreshed = await dispatch('refresh', { refresh_token })
                    if (!refreshed) {
                        return { ok: false, reason: 'refresh-failed' }
                    }
                    commit('setInitialCheck', true)
                    await loadTranslations(refreshed?.user?.locale ?? state.user?.locale)
                    return { ok: true }
                }

                const user = await dispatch('user', { token })
                if (!user) {
                    return { ok: false, reason: 'user-failed' }
                }
                await dispatch('saveUser', { user, token, refresh_token, forceExpiresIn: expiresIn })
                commit('setInitialCheck', true)
                await loadTranslations(user?.locale)
                return { ok: true }
            } finally {
                commit('setBiometricInProgress', false)
            }
        },

        /**
         * Perform a login action against the server API.
         */
        async login({ commit, dispatch }, { credentials }) {
            const response = await axios.post('/login', credentials, { withCredentials: true })
            await dispatch('saveUser', response.data)
            commit('setInitialCheck', true)
            await loadTranslations(response.data?.user?.locale)
        },

        /**
         * Registers a new user to the application.
         */
        async register({ commit, dispatch }, { credentials }) {
            const response = await axios.post('/register', credentials, { withCredentials: true })
            await dispatch('saveUser', response.data)
            commit('setInitialCheck', true)
            await loadTranslations(response.data?.user?.locale)
        },

        /**
         * Logs the user out of the application.
         */
        async logout({ commit }) {
            // Invalidate any purchases load still in flight: it must not finish
            // and commit the offerings of the account we are signing out of.
            purchasesRequestId++
            identifiedAppUserId = null

            // Deactivate this device's push token while the access token is still
            // valid. DELETE /device-tokens sits behind auth:api, and the request
            // interceptor only signs a request when the store still holds a token:
            // the revocation below and commit('clear') both make it answer 401, so
            // the device kept receiving the notifications of the account being
            // signed out of. The watcher in app.vue still calls it afterwards, and
            // finds nothing left to do.
            if (Capacitor.getPlatform() !== 'web') {
                try {
                    const { unregisterPush } = await import('@/lib/push')
                    await unregisterPush()
                } catch (error) {
                    console.warn('[Logout] Failed to unregister the push token', error)
                }
            }

            // clear the currently set user:
            Sentry.getCurrentScope().setUser('user', null)

            // Best effort server-side logout/revocation (silent fail if token already invalid)
            try {
                await axios.post('/logout')
            } catch {
                // Ignore - token may already be invalid/expired
            }

            // Clear the application user info.
            commit('clear')

            // The notifications journal and the reminders' config of the account in memory.
            try {
                const { forgetNotifications } = await import('@/lib/hooks/use-notifications')
                forgetNotifications()
            } catch (error) {
                console.warn('[Logout] Failed to forget the notifications', error)
            }

            // Purge only transient SQLite caches.
            // Keep local session recovery data (GPS / Polar) across logouts.
            try {
                const { purgeLogoutCaches } = await import('@/lib/sqlite')
                await purgeLogoutCaches()
            } catch (error) {
                console.warn('[Logout] Failed to purge SQLite caches', error)
            }

            // Clear the persistance of the data.
            await clearAuthStorage()

            loadTranslations()
        },
    },
}
